const ELLIPSIS = '…';

/**
 * Shortens an email to at most `maxLength` characters, spending the cut on the
 * local part and keeping the domain intact — `casey.mcallister@gomantle.com`
 * becomes `casey.m…@gomantle.com`.
 *
 * A trailing cut (what CSS `truncate` gives) drops the domain, which is the
 * half that distinguishes an internal tester from a real user in a dense list.
 * The local part is where the distinguishing prefix lives, so it reads fine
 * clipped.
 *
 * Falls back to a trailing cut when there is no domain to protect (no `@`, or
 * one leading the string) or when the domain alone leaves no room for even a
 * single local character.
 */
export function ellipsizeEmail(email: string, maxLength: number): string {
  if (maxLength <= 0) {
    return '';
  }
  if (email.length <= maxLength) {
    return email;
  }

  const at = email.lastIndexOf('@');
  if (at <= 0) {
    return truncateTrailing(email, maxLength);
  }

  const domain = email.slice(at);
  const localBudget = maxLength - domain.length - ELLIPSIS.length;
  if (localBudget < 1) {
    return truncateTrailing(email, maxLength);
  }
  return `${email.slice(0, localBudget)}${ELLIPSIS}${domain}`;
}

function truncateTrailing(value: string, maxLength: number): string {
  if (maxLength <= ELLIPSIS.length) {
    return value.slice(0, maxLength);
  }
  return `${value.slice(0, maxLength - ELLIPSIS.length)}${ELLIPSIS}`;
}

export default ellipsizeEmail;
